import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import { useCreateOrganisation } from '@dhl/mdp-api'
import { Box, Card } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { getErrorMessage } from '~/common/util/axiosUtil'
import { CancelDialog, OrganisationForm } from '~/modules/organisations/components'
import {
  convertToOrganisationDto,
  createOrganisationValidationSchema,
  initialOrganisation,
  type Organisation,
} from '~/modules/organisations/model'
import { useAddressService } from '~/common/components/addresses'
import { BackButton } from '~/temp-ds-component/BackButton'

export const AddOrganisationPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { addressValidationSchema, updateAddressValidationSchema } = useAddressService(
    initialOrganisation.data.address.countryCode
  )
  const { mutateAsync: postOrganisation } = useCreateOrganisation()

  const validationSchema = createOrganisationValidationSchema(t, addressValidationSchema)

  const onSubmit = async (newOrganisation: Organisation) => {
    const newOrganisationDto = convertToOrganisationDto(newOrganisation)
    try {
      const { id } = await postOrganisation({ data: newOrganisationDto })
      showSuccess(t('admin.organisationsPage.addOrganisationSuccess'))
      navigate(links.organisationDetailsLink(id))
    } catch (error) {
      const message = getErrorMessage(error, t('admin.organisationsPage.addOrganisationError'))
      showError(message)
    }
  }

  const onBack = (dirty: boolean) => (dirty ? setCancelDialogOpen(true) : navigate(links.organisationsLink()))

  return (
    <>
      <Formik
        initialValues={initialOrganisation}
        onSubmit={onSubmit}
        validateOnMount
        validationSchema={validationSchema}
      >
        {({ dirty, values }) => (
          <>
            <Box marginBottom={2}>
              <BackButton onClick={() => onBack(dirty)}>
                {t('admin.organisationsPage.organisationForm.backButtonTitle')}
              </BackButton>
            </Box>
            <Card>
              <Card.Header title={t('admin.organisationsPage.organisationForm.titleText')} />
              <Card.Content>
                <OrganisationForm
                  type="add"
                  country={values.data.address.countryCode}
                  onCancel={() => onBack(dirty)}
                  onChangeCountry={updateAddressValidationSchema}
                  organisation={values}
                />
              </Card.Content>
            </Card>
          </>
        )}
      </Formik>
      <CancelDialog
        open={cancelDialogOpen}
        onCancel={() => setCancelDialogOpen(false)}
        href={links.organisationsLink()}
      />
    </>
  )
}
